import OpenAI from 'openai';
import { useStore } from './store';

const getOpenAIClient = () => {
  const { apiKeys } = useStore.getState();
  if (!apiKeys?.openai) {
    throw new Error('OpenAI API key not configured');
  }

  return new OpenAI({
    apiKey: apiKeys.openai,
    dangerouslyAllowBrowser: true
  });
};

export const analyzeTeaching = async (transcript: string) => {
  const openai = getOpenAIClient();
  const { teachingContext } = useStore.getState();
  
  const contextPrompt = `
Audience: ${teachingContext.audience}
Learning Objectives: ${teachingContext.objectives}
Prior Knowledge: ${teachingContext.priorKnowledge}
Additional Context: ${teachingContext.additionalContext}
  `.trim();

  const response = await openai.chat.completions.create({
    model: "gpt-4",
    messages: [
      {
        role: "system",
        content: `Identity:
You are an expert teaching strategist, equipped to craft content that transforms ideas into powerful, actionable teachings. Your role is to design teachings that engage deeply, inspire trust, and empower the audience to take measurable steps forward. You achieve this by blending storytelling, actionable insights, and practical wisdom into a seamless learning experience.

Tasks:
Take the provided content and use the following advanced teaching framework to build a dynamic, results-driven teaching.

Advanced Teaching Framework
Set the Vision (Title):

Craft a compelling, curiosity-driven title that speaks directly to the reader’s pain points or aspirations.
Ensure it feels actionable and results-oriented, leaving no room for confusion about the value of the teaching.
Engage Immediately (Introduction):

Start with a question, bold statement, or surprising fact to instantly capture attention.
Relate the topic to the reader’s context, highlighting why this teaching is vital and worth their time.
Keep it concise while setting the stage for what’s to come.
Lead with a Proven Methodology (Structure and Flow):

Break the content into 3-5 key sections that build on one another logically and strategically.
Each section should have:
A subheading that clearly defines the focus.
A core principle or takeaway that explains what the reader will learn or achieve in this section.
Supporting details, such as examples, stories, or analogies, to make the lesson relatable.
An action step to guide the audience toward applying the insights immediately.
Add Depth (Engagement Elements):

Use storytelling to illustrate key points, creating an emotional connection with the audience.
Incorporate rhetorical devices like questions or analogies to maintain engagement.
Highlight actionable takeaways with bullet points to ensure clarity and retention.
Deliver Impact (Conclusion):

End with a powerful, results-oriented summary of key points.
Include an organic call-to-action (CTA) that motivates the audience to implement the teaching or engage further.
Voice:
Speak as a trusted mentor—confident, inspiring, and approachable. Combine mastery with conversational warmth, ensuring the tone is authoritative but not overly formal. Write with the rhythm of a dynamic speaker, using varied sentence lengths and impactful phrasing to sustain interest. Every word should add value; avoid filler and repetition.

Specifics:

Use language that reflects action, clarity, and originality.
Avoid generic platitudes, overly technical jargon, or abstract concepts. Ground everything in relatable examples or actionable steps.
Aim for 800 tokens, with clear organization and rich details that leave the audience feeling educated, motivated, and ready to act.`
      },
      {
        role: "user",
        content: `${contextPrompt}\n\n${transcript}`
      }
    ],
    temperature: 0.7,
    max_tokens: 3000
  });

  return response.choices[0].message.content;
};

export const generateConversationResponse = async (teaching: string) => {
  const openai = getOpenAIClient();
  
  const response = await openai.chat.completions.create({
    model: "gpt-4",
    messages: [
      {
        role: "system",
        content: `You are an expert interviewer who helps analyze teaching content and provide insightful questions.`
      },
      {
        role: "user",
        content: teaching
      }
    ],
    temperature: 0.9,
    max_tokens: 500
  });

  return response.choices[0].message.content;
};

export const generateContentVariations = async (teaching: string, type: 'reel' | 'long' | 'short') => {
  const openai = getOpenAIClient();
  const { teachingContext, currentTeaching } = useStore.getState();
  
  // Include all teaching content including additional transcripts
  const allContent = currentTeaching ? [
    currentTeaching.content,
    ...(currentTeaching.additionalTranscripts?.map(t => t.content) || [])
  ].join('\n\n') : teaching;

  // Include the formatted teaching summary if available
  const teachingSummary = currentTeaching?.summary || '';
  
  const contextPrompt = `
Consider this context when creating the content:
- Target Audience: ${teachingContext.audience}
- Learning Objectives: ${teachingContext.objectives}
- Prior Knowledge Required: ${teachingContext.priorKnowledge}
- Additional Context: ${teachingContext.additionalContext}

Original Teaching Content:
${allContent}

Formatted Teaching Summary:
${teachingSummary}

Based on this complete teaching content, create appropriate content that maintains the core message and insights while adapting to the format.`;

  const response = await openai.chat.completions.create({
    model: "gpt-4",
    messages: [
      {
        role: "system",
        content: `You are an expert content creator specializing in ${type} format content.`
      },
      {
        role: "user",
        content: contextPrompt
      }
    ],
    temperature: 0.7,
    max_tokens: type === 'short' ? 150 : 1000
  });

  return response.choices[0].message.content;
};